/** Обёртки над эндпоинтами бэкенда. */

import { request, tokens } from './client'
import type {
  AchievementItem,
  AuthResponse,
  AutoNotesResult,
  BoardScope,
  DailyChallenge,
  Difficulty,
  EconomyInfo,
  ForgivenessResult,
  GameMode,
  GameState,
  HintKind,
  HintPrices,
  HintResult,
  LeaderboardResponse,
  MoveKind,
  MoveResult,
  PlayerStats,
  ReferralStats,
  SeasonState,
  SettingsPatch,
  ShopState,
  TechniqueCatalog,
  User,
  WalletState,
} from './types'

export async function loginWithTelegram(initData: string): Promise<AuthResponse> {
  const auth = await request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: { init_data: initData },
    auth: false,
  })
  tokens.save(auth.access, auth.refresh)
  return auth
}

/** Вход в обход Telegram — только для отладки в браузере. */
export async function loginAsDev(telegramId: number): Promise<AuthResponse> {
  const auth = await request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: { dev_telegram_id: telegramId },
    auth: false,
  })
  tokens.save(auth.access, auth.refresh)
  return auth
}

export function fetchMe(): Promise<User> {
  return request<User>('/me')
}

/** Сохранить настройки. Ответ — полный профиль, второй запрос не нужен. */
export function saveSettings(patch: SettingsPatch): Promise<User> {
  return request<User>('/me', { method: 'PATCH', body: patch })
}

export function fetchTechniques(): Promise<TechniqueCatalog> {
  return request<TechniqueCatalog>('/techniques')
}

export function startGame(
  difficulty: Difficulty,
  options: { mode?: GameMode; abandonCurrent?: boolean; puzzle?: number } = {},
): Promise<GameState> {
  return request<GameState>('/games', {
    method: 'POST',
    body: {
      difficulty,
      mode: options.mode ?? 'normal',
      abandon_current: options.abandonCurrent ?? false,
      // Задача из ссылки «попробовать эту задачу». Уровень сервер возьмёт
      // из неё самой — переданный здесь он проигнорирует.
      puzzle: options.puzzle ?? null,
    },
  })
}

/** Начать заново ту же задачу — «Начать эту задачу снова» после поражения. */
export function restartGame(gameId: number, abandonCurrent = false): Promise<GameState> {
  return request<GameState>(`/games/${gameId}/restart`, {
    method: 'POST',
    body: { abandon_current: abandonCurrent },
  })
}

/**
 * Остановить или возобновить таймер.
 *
 * Пауза серверная: клиентскому «я свернул приложение» доверять нельзя,
 * иначе рекорд ставится одной строчкой в консоли браузера.
 */
export function pauseGame(gameId: number, resume = false): Promise<GameState> {
  return request<GameState>(`/games/${gameId}/pause`, {
    method: 'POST',
    body: { resume },
  })
}

export function fetchCurrentGame(mode: GameMode = 'normal'): Promise<GameState> {
  return request<GameState>(`/games/current?mode=${mode}`)
}

export function sendMove(
  gameId: number,
  move: { seq: number; cell: number; kind: MoveKind; value: number },
): Promise<MoveResult> {
  return request<MoveResult>(`/games/${gameId}/moves`, { method: 'POST', body: move })
}

export function undoMove(gameId: number): Promise<MoveResult> {
  return request<MoveResult>(`/games/${gameId}/undo`, { method: 'POST' })
}

export function redoMove(gameId: number): Promise<MoveResult> {
  return request<MoveResult>(`/games/${gameId}/redo`, { method: 'POST' })
}

export function abandonGame(gameId: number): Promise<GameState> {
  return request<GameState>(`/games/${gameId}/abandon`, { method: 'POST' })
}

/** Прайс-лист платных действий в партии и состояние бесплатной квоты. */
export function fetchHintPrices(gameId: number): Promise<HintPrices> {
  return request<HintPrices>(`/games/${gameId}/hint`)
}

export function requestHint(
  gameId: number,
  kind: HintKind,
  options: { cell?: number; requestKey?: string } = {},
): Promise<HintResult> {
  return request<HintResult>(`/games/${gameId}/hint`, {
    method: 'POST',
    body: {
      kind,
      cell: options.cell ?? null,
      // Ключ идемпотентности: без него повтор запроса при обрыве связи
      // спишет кристаллы второй раз.
      request_key: options.requestKey ?? '',
    },
  })
}

export function fetchWallet(): Promise<WalletState> {
  return request<WalletState>('/wallet')
}

/** Правила экономики: цифры приходят с сервера, а не зашиты в клиент. */
export function fetchEconomy(): Promise<EconomyInfo> {
  return request<EconomyInfo>('/economy')
}

export function fetchLeaderboard(
  scope: BoardScope,
  difficulty?: Difficulty,
): Promise<LeaderboardResponse> {
  const params = new URLSearchParams({ scope })
  if (difficulty) params.set('difficulty', difficulty)
  return request<LeaderboardResponse>(`/leaderboard?${params}`)
}

export function fetchStats(): Promise<PlayerStats> {
  return request<PlayerStats>('/stats')
}

export function fetchSeason(): Promise<SeasonState> {
  return request<SeasonState>('/season')
}

export function forgiveMistake(
  gameId: number,
  requestKey = '',
): Promise<ForgivenessResult> {
  return request<ForgivenessResult>(`/games/${gameId}/forgive`, {
    method: 'POST',
    body: { request_key: requestKey },
  })
}

export function fetchAchievements(): Promise<AchievementItem[]> {
  return request<AchievementItem[]>('/achievements')
}

export function fetchDailyChallenge(): Promise<DailyChallenge> {
  return request<DailyChallenge>('/daily')
}

export function fetchReferrals(): Promise<ReferralStats> {
  return request<ReferralStats>('/referrals')
}

export function fetchShop(): Promise<ShopState> {
  return request<ShopState>('/shop')
}

export function buyItem(code: string, requestKey = ''): Promise<ShopState> {
  return request<ShopState>(`/shop/${code}/buy`, {
    method: 'POST',
    body: { request_key: requestKey },
  })
}

export function selectTheme(code: string): Promise<ShopState> {
  return request<ShopState>('/shop/theme', { method: 'POST', body: { code } })
}

export function fillAutoNotes(gameId: number, requestKey = ''): Promise<AutoNotesResult> {
  return request<AutoNotesResult>(`/games/${gameId}/autonotes`, {
    method: 'POST',
    body: { request_key: requestKey },
  })
}
